import { css, html, nothing } from 'lit';
import { DPrepareBehavior } from './d-prepare-behavior.js';
import './d-robot-header.js';
import '../../library/elements/d-label.js';
import '../../library/editors/elements/d-select-dropdown.js';
import { customElement, property, query } from 'lit/decorators.js';
import type { AccountStateViewModel } from '@trinnvis/dabih-api-client';
import { produce } from 'immer';
import { t } from 'src/i18n';

/**
 *
 */
@customElement('d-prepare-step-1')
export class DPrepareStep1 extends DPrepareBehavior {
  static readonly styles = [
    ...super.styles,
    css`
      :host {
        display: block;
        background: var(--outskirts-background-color);
        min-height: 100vh;
      }

      .functionsAssign {
        margin-top: 16px;
      }

      .label-wrap {
        margin-right: 0;
        margin-bottom: 0;
        width: 100%;
        align-items: baseline;
        padding: 6px 0;
        display: flex;
        flex-wrap: wrap;
        position: relative;
        overflow: visible;
      }

      .label-wrap > *:first-child {
        width: 170px;
        flex: none;
      }

      .label-wrap > *:last-child {
        margin-right: 0;
        width: 300px;
        flex: 1;
      }

      @media screen and (max-width: 500px) {
        .label-wrap {
          display: block;
        }

        .label-wrap > *:first-child,
        .label-wrap > *:last-child {
          display: block;
          width: 100%;
          text-align: left;
        }
      }

      .maxWidth500 .label-wrap {
        display: block;
      }

      .maxWidth500 .label-wrap > *:first-child,
      .maxWidth500 .label-wrap > *:last-child {
        display: block;
        width: 100%;
        text-align: left;
      }

      d-select {
        display: inline-block;
      }

      d-label {
        display: inline-block;
      }
    `,
  ];
  @query('#accountConfig2Form')
  accountConfig2Form!: HTMLElement;
  @property({ type: Object })
  state: AccountStateViewModel = {
    assetFunction: '',
    computerFunction: '',
    economicsFunction: '',
    employees: [],
    healthSafetyAndEnvironmentalFunction: '',
    humanResourcesFunction: '',
    leaderFunction: '',
    professionalDevelopmentFunction: '',
    qualityManagerFunction: '',
  };
  @property({ type: Boolean })
  isPublic = true;

  protected back = 'step0';
  protected next = 'finish';

  private get robotMessage() {
    return t('account.prepareStep1.robotMessage');
  }
  private get standardResponsibilities() {
    return [
      {
        field: 'leaderFunction',
        isPublic: false,
        name: t('account.prepareStep1.responsibilityLeader'),
      },
      {
        field: 'humanResourcesFunction',
        isPublic: false,
        name: t('account.prepareStep1.responsibilityHumanResources'),
      },
      {
        field: 'economicsFunction',
        isPublic: false,
        name: t('account.prepareStep1.responsibilityEconomics'),
      },
      {
        field: 'computerFunction',
        isPublic: false,
        name: t('account.prepareStep1.responsibilityComputer'),
      },
      {
        field: 'assetFunction',
        isPublic: false,
        name: t('account.prepareStep1.responsibilityAsset'),
      },
      {
        field: 'professionalDevelopmentFunction',
        isPublic: false,
        name: t('account.prepareStep1.responsibilityProfessionalDevelopment'),
      },
      {
        field: 'healthSafetyAndEnvironmentalFunction',
        isPublic: false,
        name: t('account.prepareStep1.responsibilityHealthSafety'),
      },
      {
        field: 'qualityManagerFunction',
        isPublic: false,
        name: t('account.prepareStep1.responsibilityQuality'),
      },
    ];
  }

  private get employeeOptions() {
    return this.state.employees
      .filter((employee) => {
        return !this._stringIsEmpty(employee.firstName) || !this._stringIsEmpty(employee.lastName);
      })
      .map((employee) => {
        return {
          value: employee.uuid,
          text: employee.firstName + ' ' + employee.lastName,
        };
      });
  }

  _stringIsEmpty(value) {
    return value ? value.trim().length === 0 : true;
  }

  _shouldHide(isPublic, needPublic) {
    return needPublic && !isPublic;
  }

  _setFieldValue(e: CustomEvent<{ value: string }>) {
    const t = e.target as HTMLSelectElement;
    if (t) {
      this.state = produce(this.state, (draft) => {
        const field = t.dataset['field'];
        if (field) {
          draft[field] = e.detail.value;
        }
      });
      this.dispatchEvent(new CustomEvent('state-changed', { composed: true, bubbles: true, detail: this.state }));
    }
  }

  render() {
    return html`
      <d-robot-header .message=${this.robotMessage}></d-robot-header>
      <div class="outskirtsContent">
        <h2>${t('account.prepareStep1.heading')}</h2>
        <form id="accountConfig2Form" @submit=${(e) => this._finish(e)}>
          <div class="functionsAssign">
            ${this.standardResponsibilities.map(
              (item) =>
                html` ${this._shouldHide(this.isPublic, item.isPublic)
                  ? nothing
                  : html`
                      <div class="label-wrap">
                        <d-label label="${item.name}"></d-label>
                        <d-select-dropdown
                          outskirts
                          .options=${this.employeeOptions}
                          .value=${this.state[item.field]}
                          data-field="${item.field}"
                          @value-changed=${(e) => this._setFieldValue(e)}
                        ></d-select-dropdown>
                      </div>
                    `}`,
            )}
          </div>

          <div class="buttons">
            <button @click=${(e) => this._backward(e)} type="button">${t('account.prepareStep1.previous')}</button>
            <button class="right" @click=${(e) => this._cancel(e)} type="button">
              ${t('account.prepareStep1.cancel')}
            </button>
            <button class="right nextPage" type="submit">${t('account.prepareStep1.next')}</button>
          </div>
        </form>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-prepare-step-1': DPrepareStep1;
  }
}
